import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Phone, Mail, User, HardHat } from 'lucide-react';
import api from '../../api/auth.api';

interface StartupTask {
  task_type_id: number;
  assigned_company: string | null;
  assigned_contact: string | null;
  assigned_phone: string | null;
  assigned_email: string | null;
  type_name: string;
  type_code: string;
}

interface TaskType {
  id: number;
  type_name: string;
  type_code: string;
}

interface CategoryGroup {
  title: string;
  codes: string[];
}

const CATEGORIES: CategoryGroup[] = [
  {
    title: 'Survey & Testing',
    codes: ['survey_layout', 'testing']
  },
  {
    title: 'Materials & Trucking',
    codes: ['fill_dirt', 'base_rock', 'trucking', 'dumpsters', 'asphalt_supplier']
  },
  {
    title: 'Subcontractors',
    codes: [
      'underground_utilities', 'concrete_curbs_sidewalks', 'clearing', 'sod',
      'erosion_control_install', 'erosion_control_maintenance', 'asphalt_paving',
      'building_demolition', 'site_demolition'
    ]
  }
];

const PLACEHOLDER_LABELS = ['Not Required', 'By General Contractor/Owner'];

const JobSubcontractors: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const jobId = (location.state as any)?.jobId;

  const [loading, setLoading] = useState(true);
  const [jobName, setJobName] = useState('');
  const [tasks, setTasks] = useState<StartupTask[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setError('No job selected');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const [jobRes, startupRes, typesRes] = await Promise.all([
          api.get(`/api/jobs/${jobId}`),
          api.get(`/api/job-startup/job/${jobId}`),
          api.get('/api/job-startup/task-types')
        ]);

        // Job name
        if (jobRes.data.success) {
          setJobName(jobRes.data.data.job_name || '');
        }

        // Merge task data with type metadata
        const taskTypes: TaskType[] = typesRes.data.success ? typesRes.data.data : [];
        const rawTasks = startupRes.data.success ? startupRes.data.data.tasks : [];

        const merged: StartupTask[] = rawTasks.map((t: any) => {
          const typeInfo = taskTypes.find((tt) => tt.id === t.task_type_id);
          return {
            ...t,
            type_name: t.type_name || typeInfo?.type_name || '',
            type_code: t.type_code || typeInfo?.type_code || ''
          };
        });

        setTasks(merged);
      } catch (err) {
        console.error('Error loading subcontractors:', err);
        setError('Failed to load subcontractors');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [jobId]);

  // Filter tasks that have a real assignment
  const hasAssignment = (task: StartupTask): boolean => {
    const company = task.assigned_company?.trim();
    if (!company || company === 'Hidden') return false;
    return true;
  };

  const isPlaceholder = (company: string): boolean => {
    return PLACEHOLDER_LABELS.some(label =>
      company.toLowerCase() === label.toLowerCase()
    );
  };

  const assignedTasks = tasks.filter(hasAssignment);

  // Group assigned tasks by category
  const groupedCategories = CATEGORIES.map(cat => {
    const catTasks = assignedTasks.filter(t => cat.codes.includes(t.type_code));
    return { ...cat, tasks: catTasks };
  }).filter(cat => cat.tasks.length > 0);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }}
        />
        <div style={{ color: '#64748b', fontSize: 16, marginTop: 16, fontWeight: '500' }}>
          Loading subcontractors...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px'
          }}
        >
          <ArrowLeft size={22} color="#1e293b" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
            Subcontractors
          </h1>
          {jobName && (
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
              {jobName}
            </p>
          )}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Error */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#dc2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Empty state */}
        {!error && groupedCategories.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#64748b'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <HardHat size={32} color="#94a3b8" />
            </div>
            <div style={{ fontSize: '16px', fontWeight: '500' }}>
              No subcontractors assigned yet
            </div>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>
              Assignments are managed in the admin portal
            </div>
          </div>
        )}

        {/* Category sections */}
        {groupedCategories.map((cat) => (
          <div key={cat.title} style={{ marginBottom: '16px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 8px 4px'
            }}>
              {cat.title}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {cat.tasks.map((task, idx) => {
                const company = task.assigned_company?.trim() || '';
                const placeholder = isPlaceholder(company);

                return (
                  <div
                    key={`${task.type_code}-${idx}`}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    {/* Task type label */}
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '6px'
                    }}>
                      {task.type_name}
                    </div>

                    {/* Company name */}
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: placeholder ? '#94a3b8' : '#1e293b',
                      fontStyle: placeholder ? 'italic' : 'normal'
                    }}>
                      {company}
                    </div>

                    {/* Contact details - only show for real assignments */}
                    {!placeholder && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {task.assigned_contact && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={14} color="#94a3b8" />
                            <span style={{ fontSize: '14px', color: '#475569' }}>
                              {task.assigned_contact}
                            </span>
                          </div>
                        )}
                        {task.assigned_phone && (
                          <a
                            href={`tel:${task.assigned_phone}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              textDecoration: 'none'
                            }}
                          >
                            <Phone size={14} color="#3b82f6" />
                            <span style={{ fontSize: '14px', color: '#3b82f6' }}>
                              {task.assigned_phone}
                            </span>
                          </a>
                        )}
                        {task.assigned_email && (
                          <a
                            href={`mailto:${task.assigned_email}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              textDecoration: 'none'
                            }}
                          >
                            <Mail size={14} color="#3b82f6" />
                            <span style={{ fontSize: '14px', color: '#3b82f6' }}>
                              {task.assigned_email}
                            </span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobSubcontractors;
